import { Injectable, inject } from '@angular/core';
import { type Observable } from 'rxjs';
import {
  SbeSmsApiService,
  type CreateMessageBodyDto,
  type CreatedMessageResponseDto,
  type ListMessageDetailsResponseDto,
  type ListMessagesResponseDto,
  type MessageDetailDto,
  type MessageStatus,
} from '@sbe-spinoff-client/sbe-sms-api';

/**
 * 메시지(/messages) 호출 facade. orval 이 만든 서비스를 그대로 노출하면
 * controller suffix 가 붙은 메서드 이름이 컴포넌트에 새어나와 readability 가
 * 떨어지므로, web 쪽 의미명으로 1:1 wrap 한다.
 *
 * org 라우트(`orgId` 있음)는 URL이 `/orgs/:orgId/...`이라 `spinoffAuthInterceptor`가
 * `resolveOrgScopedId`로 자동 인식해 org access token을 붙인다(2026-07-23) — 여기서
 * 토큰을 신경 쓸 필요 없이 orgId 유무로 org/personal 메서드만 고르면 된다.
 */
@Injectable({ providedIn: 'root' })
export class MessagesApiService {
  private readonly api = inject(SbeSmsApiService);

  create(
    body: CreateMessageBodyDto,
    orgId?: string,
  ): Observable<CreatedMessageResponseDto> {
    return orgId
      ? this.api.orgMessagesControllerCreate<CreatedMessageResponseDto>(
          orgId,
          body,
        )
      : this.api.messagesControllerCreate<CreatedMessageResponseDto>(body);
  }

  list(params: {
    cursor?: string;
    limit?: number;
    orgId?: string;
    status?: MessageStatus[];
    createdAtFrom?: string;
    createdAtTo?: string;
    text?: string;
    sender?: string;
    originator?: string;
    recipient?: string;
  }): Observable<ListMessagesResponseDto> {
    const { orgId, ...rest } = params;
    const query = {
      ...(rest.cursor !== undefined ? { cursor: rest.cursor } : {}),
      ...(rest.limit !== undefined ? { limit: rest.limit } : {}),
      ...(rest.status?.length ? { status: rest.status } : {}),
      ...(rest.createdAtFrom !== undefined ? { createdAtFrom: rest.createdAtFrom } : {}),
      ...(rest.createdAtTo !== undefined ? { createdAtTo: rest.createdAtTo } : {}),
      ...(rest.text !== undefined ? { text: rest.text } : {}),
      ...(rest.sender !== undefined ? { sender: rest.sender } : {}),
      ...(rest.originator !== undefined ? { originator: rest.originator } : {}),
      ...(rest.recipient !== undefined ? { recipient: rest.recipient } : {}),
    };
    return orgId
      ? this.api.orgMessagesControllerList<ListMessagesResponseDto>(
          orgId,
          query,
        )
      : this.api.messagesControllerList<ListMessagesResponseDto>(query);
  }

  getById(id: string, orgId?: string): Observable<MessageDetailDto> {
    return orgId
      ? this.api.orgMessagesControllerGetById<MessageDetailDto>(orgId, id)
      : this.api.messagesControllerGetById<MessageDetailDto>(id);
  }

  listDetails(
    id: string,
    params: { cursor?: string; limit?: number },
    orgId?: string,
  ): Observable<ListMessageDetailsResponseDto> {
    const query = {
      ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
      ...(params.limit !== undefined ? { limit: params.limit } : {}),
    };
    return orgId
      ? this.api.orgMessagesControllerListDetails<ListMessageDetailsResponseDto>(
          orgId,
          id,
          query,
        )
      : this.api.messagesControllerListDetails<ListMessageDetailsResponseDto>(
          id,
          query,
        );
  }

  cancel(id: string, orgId?: string): Observable<void> {
    return orgId
      ? this.api.orgMessagesControllerCancel<void>(orgId, id)
      : this.api.messagesControllerCancel<void>(id);
  }
}
